import { useContext, useState } from 'react';

import { Box, Tab, TabList, Table, Tabs, Tbody, Tooltip } from '@chakra-ui/react';
import { MdAccountCircle } from 'react-icons/md';

import { Address } from '../../gql/addressGql';
import { getFullNameWithMiddle } from '../../gql/customerGql';
import { DealStateEnum } from '../../gql/dealGql';
import { Country, DealType } from '../../gql/generated/graphql';

import CreditApplication from '../CreditApplication/CreditApplication';
import CustomerContact from '../CustomerContact';
import { InfoCardValue, InfoRow } from '../InfoCard/InfoCardV2';
import MaskedSsnCustomerInfo from '../MaskedSsn/MaskedSsnCustomerInfo';
import Card from '../shared/Card';
import CardHeaderV2 from '../shared/Card/components/CardHeaderV2';

import './CustomerInfo.css';

import {
  COUNTRY_LABELS,
  addressConfigForCountry,
  countryFromString,
  countryRulesFor,
  isCanada,
} from '../../constants/countries';
import { DealContext } from '../../libs/DealContext';
import { ModalContext } from '../../libs/contextLib';

export type IBuyerInfo = Record<string, InfoCardValue | undefined>;

type ICobuyerInfo = Record<string, InfoCardValue | undefined>;

const addressInfo = (
  address: Address | undefined,
  country: Country,
  prefix = '',
): Record<string, InfoCardValue | undefined> => {
  const { regionLabel, postalCodeLabel } = addressConfigForCountry(country);
  return {
    [`${prefix}Address Line 1`]: address?.address_line,
    [`${prefix}Apt., Suite, Unit #`]: address?.address_line_2,
    [`${prefix}City`]: address?.city,
    ...(isCanada(country) ? { [`${prefix}Country`]: COUNTRY_LABELS[country] } : {}),
    [`${prefix}${regionLabel}`]: address?.state,
    [`${prefix}${postalCodeLabel}`]: address?.zip,
  };
};

const CustomerInfo = () => {
  const { deal } = useContext(DealContext);
  const { modals, setModal } = useContext(ModalContext);

  const getEmailComponent = (email?: string, auth0Id?: string) => (
    <Box display="flex" alignItems="center">
      <Box mr={1}>{email}</Box>
      {email && auth0Id && !auth0Id.startsWith('anon') ? (
        <Tooltip label="This email has a linked web account" placement="top" hasArrow>
          <Box minW={4}>
            <MdAccountCircle color="#32CDD4" size={24} />
          </Box>
        </Tooltip>
      ) : null}
    </Box>
  );

  const buyerInfo: IBuyerInfo = {
    Name: getFullNameWithMiddle(deal.customer),
    ...addressInfo(
      deal.customer?.address,
      countryFromString(deal.customer?.address?.country ?? deal.country),
    ),
    Phone: deal.customer?.phone_number,
    ...(countryRulesFor(deal.country).requiresSsn
      ? { SSN: { render: <MaskedSsnCustomerInfo customer={deal.customer} />, value: '' } }
      : {}),
    Email: {
      render: getEmailComponent(deal.customer?.email, deal.customer?.auth0_id),
      value: deal.customer?.email,
    },
  };

  let coBuyerInfo: ICobuyerInfo = {};
  if (deal.cobuyer) {
    coBuyerInfo = {
      'CB Name': getFullNameWithMiddle(deal.cobuyer),
      ...addressInfo(
        deal.cobuyer.address,
        countryFromString(deal.cobuyer.address?.country ?? deal.country),
        'CB ',
      ),
      'CB Phone': deal.cobuyer.phone_number,
      'CB Email': {
        render: getEmailComponent(deal.cobuyer.email, deal.cobuyer.auth0_id),
        value: deal.cobuyer.email,
      },
    };
  }

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const selectedInfo: IBuyerInfo | ICobuyerInfo = selectedTabIndex === 0 ? buyerInfo : coBuyerInfo;

  return (
    <>
      <Card variant="roundedNoMargin">
        <CardHeaderV2
          title="Customer Info"
          onClick={
            deal.type === DealType.Marketplace || deal.state === DealStateEnum.SoftClose
              ? undefined
              : () => setModal({ CustomerInfo: true })
          }
        />
        <Tabs
          index={selectedTabIndex}
          onChange={(index) => setSelectedTabIndex(index)}
          variant="enclosed"
        >
          <TabList bgColor="queenBlue" pt={1}>
            <Tab
              bgColor={selectedTabIndex === 0 ? 'white' : 'gray.200'}
              borderRadius="8px 8px 0 0"
              fontWeight="bold"
              ml={1}
              px={8}
            >
              Buyer
            </Tab>
            {deal.cobuyer && (
              <Tab
                bgColor={selectedTabIndex === 1 ? 'white' : 'gray.200'}
                borderRadius="8px 8px 0 0"
                fontWeight="bold"
                ml={1.5}
                px={8}
              >
                Co-Buyer
              </Tab>
            )}
          </TabList>
        </Tabs>
        <Table mt={3}>
          <Tbody>
            {Object.entries(selectedInfo).map(([key, value]) => (
              <InfoRow key={key} name={key} value={value} />
            ))}
          </Tbody>
        </Table>
        <CustomerContact />
      </Card>

      {modals.CustomerInfo && <CreditApplication showInsideModal />}
    </>
  );
};

export default CustomerInfo;
